/**
 * Collection Sort Feature
 * Handles desktop dropdown, mobile bottom-sheet, AJAX sort, URL state
 * @version 1.0
 */
(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    selectors: {
      toolbar: '[data-sort-toolbar]',
      trigger: '[data-sort-trigger]',
      triggerMobile: '[data-sort-trigger-mobile]',
      dropdown: '[data-sort-dropdown]',
      dropdownOption: '[data-sort-option]',
      modal: '[data-sort-modal]',
      modalBackdrop: '[data-sort-modal-backdrop]',
      modalClose: '[data-sort-modal-close]',
      modalOption: '[data-sort-option-mobile]',
      dragHandle: '[data-sort-drag-handle]',
      currentLabel: '[data-sort-current-label]',
      announcement: '[data-sort-announcement]',
      productsGrid: '[data-products-grid], #product-grid, .mrk-product-list',
      gridWrapper: '[data-sort-grid-wrapper]',
      skeleton: '[data-sort-skeleton]',
      loadingOverlay: '[data-sort-loading-overlay]',
      viewToggle: '[data-sort-view-toggle]',
      viewBtn: '[data-grid-view]'
    },
    gridViewStorageKey: 'jrs_collection_product_grid_view',
    gridViewValues: ['1', '2'],
    classes: {
      active: 'sort-trigger--active',
      activeModal: 'sort-trigger-mobile--active',
      expanded: 'is-expanded',
      loading: 'is-loading',
      flipped: 'sort-dropdown--flipped',
      sticky: 'sort-toolbar--sticky',
      bodyLock: 'sort-modal-open',
      viewBtnActive: 'sort-toolbar__view-btn--active'
    },
    timings: {
      debounce: 300,
      skeletonDelay: 300,
      stillLoadingDelay: 10000,
      errorDismiss: 5000,
      transitionDuration: 250
    }
  };

  // State
  let state = {
    currentSort: 'manual',
    previousSort: 'manual',
    collectionHandle: '',
    isLoading: false,
    abortController: null,
    scrollPosition: 0,
    timers: {
      debounce: null,
      skeleton: null,
      stillLoading: null
    },
    touchStart: { y: 0, time: 0 },
    isMobile: window.innerWidth < 768
  };

  // Elements cache
  let elements = {};

  /**
   * Initialize
   */
  function init() {
    cacheElements();
    if (!elements.toolbar) return;

    state.collectionHandle = elements.toolbar.dataset.collectionHandle || '';
    state.currentSort = elements.toolbar.dataset.currentSort || 'manual';
    state.previousSort = state.currentSort;

    bindEvents();
    handleInitialUrl();
    updateDeviceState();
    setupStickyToolbar();
    applyMobileGridView();
  }

  /**
   * Cache DOM elements
   */
  function cacheElements() {
    elements.toolbar = document.querySelector(CONFIG.selectors.toolbar);
    elements.trigger = document.querySelector(CONFIG.selectors.trigger);
    elements.triggerMobile = document.querySelector(CONFIG.selectors.triggerMobile);
    elements.dropdown = document.querySelector(CONFIG.selectors.dropdown);
    elements.modal = document.querySelector(CONFIG.selectors.modal);
    elements.modalBackdrop = document.querySelector(CONFIG.selectors.modalBackdrop);
    elements.modalClose = document.querySelector(CONFIG.selectors.modalClose);
    elements.dragHandle = document.querySelector(CONFIG.selectors.dragHandle);
    elements.currentLabel = document.querySelector(CONFIG.selectors.currentLabel);
    elements.announcement = document.querySelector(CONFIG.selectors.announcement);
    elements.productsGrid = document.querySelector(CONFIG.selectors.productsGrid);
    elements.skeleton = document.querySelector(CONFIG.selectors.skeleton);
    elements.loadingOverlay = document.querySelector(CONFIG.selectors.loadingOverlay);
    elements.viewToggle = elements.toolbar ? elements.toolbar.querySelector(CONFIG.selectors.viewToggle) : null;
  }

  /**
   * Bind event listeners
   */
  function bindEvents() {
    // Desktop dropdown
    if (elements.trigger) {
      elements.trigger.addEventListener('click', toggleDropdown);
      elements.trigger.addEventListener('keydown', handleTriggerKeydown);
    }

    if (elements.dropdown) {
      const options = elements.dropdown.querySelectorAll(CONFIG.selectors.dropdownOption);
      options.forEach(option => {
        option.addEventListener('click', () => handleOptionSelect(option));
        option.addEventListener('keydown', (e) => handleOptionKeydown(e, option));
      });
    }

    // Mobile bottom-sheet
    if (elements.triggerMobile) {
      elements.triggerMobile.addEventListener('click', openModal);
    }

    if (elements.modalBackdrop) {
      elements.modalBackdrop.addEventListener('click', closeModal);
    }

    if (elements.modalClose) {
      elements.modalClose.addEventListener('click', closeModal);
    }

    if (elements.modal) {
      const modalOptions = elements.modal.querySelectorAll(CONFIG.selectors.modalOption);
      modalOptions.forEach(option => {
        option.addEventListener('change', () => handleModalOptionSelect(option));
      });

      // Swipe to dismiss
      setupSwipeToDismiss();
    }

    // Click outside to close dropdown
    document.addEventListener('click', handleClickOutside);

    // Escape key
    document.addEventListener('keydown', handleEscapeKey);

    // Browser back/forward
    window.addEventListener('popstate', handlePopState);

    // Resize
    window.addEventListener('resize', debounce(() => {
      updateDeviceState();
    }, 250));

    // Mobile grid view toggle (1 or 2 per row)
    if (elements.toolbar) {
      elements.toolbar.addEventListener('click', function(e) {
        const btn = e.target.closest(CONFIG.selectors.viewBtn);
        if (btn && CONFIG.gridViewValues.includes(btn.dataset.gridView)) {
          e.preventDefault();
          const previousView = getMobileGridView();
          setMobileGridView(btn.dataset.gridView);
          trackGA4Event('sort_grid_view_toggle', {
            view_mode: btn.dataset.gridView,
            previous_view: previousView,
            device_type: state.isMobile ? 'mobile' : 'desktop'
          });
        }
      });
    }
  }

  /**
   * Get current mobile grid view (from localStorage or default from section)
   */
  function getMobileGridView() {
    const saved = localStorage.getItem(CONFIG.gridViewStorageKey);
    if (saved && CONFIG.gridViewValues.includes(saved)) return saved;
    const defaultVal = elements.toolbar && elements.toolbar.dataset.defaultMobileGridView;
    return (defaultVal && CONFIG.gridViewValues.includes(defaultVal)) ? defaultVal : '2';
  }

  /**
   * Apply mobile grid view to grid and update toggle buttons
   */
  function applyMobileGridView() {
    const value = getMobileGridView();
    if (elements.productsGrid) {
      elements.productsGrid.dataset.mobileCols = value;
    }
    const container = elements.viewToggle || (elements.toolbar && elements.toolbar.querySelector(CONFIG.selectors.viewToggle));
    if (container) {
      const buttons = container.querySelectorAll(CONFIG.selectors.viewBtn);
      buttons.forEach(btn => {
        const isActive = btn.dataset.gridView === value;
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        btn.classList.toggle(CONFIG.classes.viewBtnActive, isActive);
      });
    }
  }

  /**
   * Set mobile grid view and persist to localStorage
   */
  function setMobileGridView(value) {
    if (!CONFIG.gridViewValues.includes(value)) return;
    localStorage.setItem(CONFIG.gridViewStorageKey, value);
    applyMobileGridView();
  }

  /**
   * Handle initial URL on page load
   */
  function handleInitialUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const sortParam = urlParams.get('sort_by');
    
    if (sortParam && sortParam !== state.currentSort) {
      state.currentSort = sortParam;
      state.previousSort = sortParam;
      updateUIForCurrentSort();
    }
  }

  /**
   * Update device state (mobile vs desktop)
   */
  function updateDeviceState() {
    state.isMobile = window.innerWidth < 768;
  }

  /**
   * Setup sticky toolbar behavior
   */
  function setupStickyToolbar() {
    if (!elements.toolbar) return;

    let wasSticky = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldBeSticky = !entry.isIntersecting && state.isMobile;
        if (shouldBeSticky) {
          elements.toolbar.classList.add(CONFIG.classes.sticky);
          if (!wasSticky) {
            wasSticky = true;
            trackGA4Event('sort_toolbar_sticky', { is_sticky: true });
          }
        } else {
          elements.toolbar.classList.remove(CONFIG.classes.sticky);
          if (wasSticky) {
            wasSticky = false;
            trackGA4Event('sort_toolbar_sticky', { is_sticky: false });
          }
        }
      },
      { threshold: [0], rootMargin: '-1px 0px 0px 0px' }
    );

    observer.observe(elements.toolbar);
  }

  /**
   * Toggle desktop dropdown
   */
  function toggleDropdown(e) {
    e.stopPropagation();
    
    if (!elements.dropdown) return;

    const isExpanded = elements.trigger.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      closeDropdown();
      trackGA4Event('sort_closed_without_change');
    } else {
      openDropdown();
      trackGA4Event('sort_opened');
    }
  }

  /**
   * Open desktop dropdown
   */
  function openDropdown() {
    if (!elements.dropdown || !elements.trigger) return;

    elements.dropdown.hidden = false;
    elements.trigger.setAttribute('aria-expanded', 'true');
    
    // Check for collision and flip if needed
    const dropdownRect = elements.dropdown.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    
    if (dropdownRect.bottom > viewportHeight - 20) {
      elements.dropdown.classList.add(CONFIG.classes.flipped);
    } else {
      elements.dropdown.classList.remove(CONFIG.classes.flipped);
    }

    // Focus current selected option or first option
    const selectedOption = elements.dropdown.querySelector('[aria-selected="true"]');
    const firstOption = elements.dropdown.querySelector(CONFIG.selectors.dropdownOption);
    const optionToFocus = selectedOption || firstOption;
    
    if (optionToFocus) {
      setTimeout(() => optionToFocus.focus(), 50);
    }
  }

  /**
   * Close desktop dropdown
   */
  function closeDropdown() {
    if (!elements.dropdown || !elements.trigger) return;

    elements.dropdown.hidden = true;
    elements.trigger.setAttribute('aria-expanded', 'false');
    elements.trigger.focus();
  }

  /**
   * Open mobile modal
   */
  function openModal() {
    if (!elements.modal || !elements.modalBackdrop) return;

    elements.modal.hidden = false;
    elements.modalBackdrop.hidden = false;
    lockBodyScroll();
    
    trackGA4Event('sort_opened');

    // Focus first checked radio or first radio
    const checkedRadio = elements.modal.querySelector('input[type="radio"]:checked');
    const firstRadio = elements.modal.querySelector('input[type="radio"]');
    const radioToFocus = checkedRadio || firstRadio;
    
    if (radioToFocus) {
      setTimeout(() => radioToFocus.focus(), CONFIG.timings.transitionDuration);
    }
  }

  /**
   * Close mobile modal
   */
  function closeModal() {
    if (!elements.modal || !elements.modalBackdrop) return;

    elements.modal.hidden = true;
    elements.modalBackdrop.hidden = true;
    unlockBodyScroll();
    
    if (elements.triggerMobile) {
      elements.triggerMobile.focus();
    }

    // Check if sort changed
    const checkedRadio = elements.modal.querySelector('input[type="radio"]:checked');
    if (checkedRadio && checkedRadio.value === state.currentSort) {
      trackGA4Event('sort_closed_without_change');
    }
  }

  /**
   * Setup swipe-to-dismiss for mobile modal
   */
  function setupSwipeToDismiss() {
    if (!elements.modal) return;

    let startY = 0;
    let currentY = 0;
    let isDragging = false;

    elements.modal.addEventListener('touchstart', (e) => {
      startY = e.touches[0].clientY;
      currentY = startY;
      isDragging = true;
    }, { passive: true });

    elements.modal.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      
      currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;
      
      // Only allow downward swipe
      if (deltaY > 0) {
        elements.modal.style.transform = `translateY(${deltaY}px)`;
      }
    }, { passive: true });

    elements.modal.addEventListener('touchend', () => {
      if (!isDragging) return;
      
      const deltaY = currentY - startY;
      isDragging = false;
      
      // Dismiss if swiped > 100px
      if (deltaY > 100) {
        trackGA4Event('sort_modal_dismiss', {
          dismiss_method: 'swipe',
          device_type: 'mobile'
        });
        closeModal();
      }
      
      // Reset transform
      elements.modal.style.transform = '';
    });
  }

  /**
   * Lock body scroll (iOS compatible)
   */
  function lockBodyScroll() {
    state.scrollPosition = window.pageYOffset;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${state.scrollPosition}px`;
    document.body.style.width = '100%';
    document.body.classList.add(CONFIG.classes.bodyLock);
  }

  /**
   * Unlock body scroll
   */
  function unlockBodyScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.classList.remove(CONFIG.classes.bodyLock);
    window.scrollTo(0, state.scrollPosition);
  }

  /**
   * Handle dropdown option selection
   */
  function handleOptionSelect(option) {
    const sortValue = option.dataset.sortOption;
    if (sortValue === state.currentSort) {
      closeDropdown();
      return;
    }

    applySort(sortValue);
    closeDropdown();
  }

  /**
   * Handle modal radio option selection
   */
  function handleModalOptionSelect(option) {
    const sortValue = option.value;
    if (sortValue === state.currentSort) {
      closeModal();
      return;
    }

    applySort(sortValue);
    
    // Close modal after short delay for visual feedback
    setTimeout(() => {
      closeModal();
    }, 150);
  }

  /**
   * Apply sort
   */
  function applySort(sortValue) {
    if (state.isLoading) {
      // Cancel previous request
      if (state.abortController) {
        state.abortController.abort();
      }
    }

    // Clear existing timers
    clearAllTimers();

    // Debounce rapid selections
    if (state.timers.debounce) {
      clearTimeout(state.timers.debounce);
    }

    state.timers.debounce = setTimeout(() => {
      executeSortChange(sortValue);
    }, CONFIG.timings.debounce);
  }

  /**
   * Execute sort change with AJAX
   */
  async function executeSortChange(sortValue) {
    state.previousSort = state.currentSort;
    state.currentSort = sortValue;
    state.isLoading = true;

    // Show loading state
    showLoadingState();

    // Setup AbortController
    state.abortController = new AbortController();

    // Build URL
    const url = buildSortUrl(sortValue);

    try {
      const response = await fetch(url, {
        signal: state.abortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      
      // Parse and update grid
      await updateProductGrid(html);

      // Update URL
      updateUrl(sortValue);

      // Update UI
      updateUIForCurrentSort();

      // Scroll to grid top
      scrollToGrid();

      // Track GA4
      trackGA4Event('sort_applied', {
        sort_option: sortValue,
        previous_sort: state.previousSort
      });

      // Announce to screen readers
      announceSort(sortValue);

    } catch (error) {
      if (error.name === 'AbortError') {
        // console.log('Sort request aborted');
      } else {
        console.error('Sort failed:', error);
        handleSortError();
      }
    } finally {
      hideLoadingState();
      state.isLoading = false;
      state.abortController = null;
    }
  }

  /**
   * Build sort URL
   */
  function buildSortUrl(sortValue) {
    const baseUrl = `/collections/${state.collectionHandle}`;
    const url = new URL(baseUrl, window.location.origin);
    
    // Preserve existing query params
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.forEach((value, key) => {
      if (key !== 'sort_by') {
        url.searchParams.set(key, value);
      }
    });

    // Set sort param
    if (sortValue !== 'manual') {
      url.searchParams.set('sort_by', sortValue);
    }

    // Add section_id for partial rendering
    url.searchParams.set('section_id', 'main-collection-product-grid');

    return url.toString();
  }

  /**
   * Update product grid with new HTML
   */
  async function updateProductGrid(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Find new grid
    const newGrid = doc.querySelector(CONFIG.selectors.productsGrid);
    
    if (newGrid && elements.productsGrid) {
      elements.productsGrid.innerHTML = newGrid.innerHTML;
      
      // Reinitialize scripts
      reinitializeScripts();

      // Re-apply mobile grid view (1 or 2 columns) after AJAX update
      applyMobileGridView();
    }
  }

  /**
   * Reinitialize scripts after grid update
   */
  function reinitializeScripts() {
    // Reinitialize VAT toggle
    reinitializeVATToggle();

    // Reinitialize swatches
    if (typeof initSwatches === 'function') {
      setTimeout(() => initSwatches(), 100);
    }

    // Product card optimization should be handled by MutationObserver
  }

  /**
   * Reinitialize VAT toggle (from existing pattern)
   */
  function reinitializeVATToggle() {
    if (typeof jQuery === 'undefined' || !jQuery.fn || !jQuery('#price-toggle').length) {
      return;
    }

    const session_key = 'exclVATv3';
    let session_val = sessionStorage.getItem(session_key);

    if (session_val == null) {
      session_val = true;
      sessionStorage.setItem(session_key, session_val);
    } else {
      session_val = (session_val == 'true');
    }

    jQuery('[data-price-wrapper]').each(function(i, p) {
      if (session_val) {
        jQuery(p).find('.in-vat').addClass('hidden');
        jQuery(p).find('.ex-vat').removeClass('hidden');
      } else {
        jQuery(p).find('.ex-vat').addClass('hidden');
        jQuery(p).find('.in-vat').removeClass('hidden');
      }
    });
  }

  /**
   * Update URL via pushState
   */
  function updateUrl(sortValue) {
    const url = new URL(window.location);
    
    if (sortValue === 'manual') {
      url.searchParams.delete('sort_by');
    } else {
      url.searchParams.set('sort_by', sortValue);
    }

    history.pushState({ sort: sortValue }, '', url.toString());
  }

  /**
   * Handle browser back/forward
   */
  function handlePopState(e) {
    const urlParams = new URLSearchParams(window.location.search);
    const sortParam = urlParams.get('sort_by') || 'manual';
    
    if (sortParam !== state.currentSort) {
      state.currentSort = sortParam;
      updateUIForCurrentSort();
      // Optionally: fetch and update grid
      executeSortChange(sortParam);
    }
  }

  /**
   * Update UI elements for current sort
   */
  function updateUIForCurrentSort() {
    const sortLabel = getSortLabel(state.currentSort);

    // Update trigger label
    if (elements.currentLabel) {
      elements.currentLabel.textContent = sortLabel;
    }

    // Update trigger active class
    if (elements.trigger) {
      if (state.currentSort !== 'manual') {
        elements.trigger.classList.add(CONFIG.classes.active);
      } else {
        elements.trigger.classList.remove(CONFIG.classes.active);
      }
    }

    if (elements.triggerMobile) {
      if (state.currentSort !== 'manual') {
        elements.triggerMobile.classList.add(CONFIG.classes.activeModal);
      } else {
        elements.triggerMobile.classList.remove(CONFIG.classes.activeModal);
      }
    }

    // Update dropdown selected state
    if (elements.dropdown) {
      const options = elements.dropdown.querySelectorAll(CONFIG.selectors.dropdownOption);
      options.forEach(option => {
        const isSelected = option.dataset.sortOption === state.currentSort;
        option.classList.toggle('sort-option--selected', isSelected);
        option.setAttribute('aria-selected', isSelected);
        option.setAttribute('tabindex', isSelected ? '0' : '-1');
      });
    }

    // Update modal radio state
    if (elements.modal) {
      const radios = elements.modal.querySelectorAll(CONFIG.selectors.modalOption);
      radios.forEach(radio => {
        radio.checked = (radio.value === state.currentSort);
      });
    }
  }

  /**
   * Get human-readable sort label
   */
  function getSortLabel(sortValue) {
    const labels = {
      'manual': 'Recommended',
      'price-ascending': 'Price: Low to High',
      'price-descending': 'Price: High to Low',
      'best-selling': 'Best Selling',
      'created-descending': 'Newest'
    };
    return labels[sortValue] || 'Recommended';
  }

  /**
   * Show loading state
   */
  function showLoadingState() {
    // Fade grid
    if (elements.productsGrid) {
      elements.productsGrid.classList.add(CONFIG.classes.loading);
    }

    // Show skeleton after 300ms
    state.timers.skeleton = setTimeout(() => {
      if (elements.skeleton && elements.productsGrid) {
        elements.skeleton.hidden = false;
        elements.productsGrid.style.display = 'none';
      }
    }, CONFIG.timings.skeletonDelay);

    // Show "Still loading..." after 10s
    state.timers.stillLoading = setTimeout(() => {
      if (elements.loadingOverlay) {
        elements.loadingOverlay.hidden = false;
      }
    }, CONFIG.timings.stillLoadingDelay);
  }

  /**
   * Hide loading state
   */
  function hideLoadingState() {
    clearAllTimers();

    if (elements.productsGrid) {
      elements.productsGrid.classList.remove(CONFIG.classes.loading);
      elements.productsGrid.style.display = '';
    }

    if (elements.skeleton) {
      elements.skeleton.hidden = true;
    }

    if (elements.loadingOverlay) {
      elements.loadingOverlay.hidden = true;
    }
  }

  /**
   * Clear all timers
   */
  function clearAllTimers() {
    Object.values(state.timers).forEach(timer => {
      if (timer) clearTimeout(timer);
    });
    state.timers = {
      debounce: null,
      skeleton: null,
      stillLoading: null
    };
  }

  /**
   * Handle sort error
   */
  function handleSortError() {
    // Revert to previous sort
    state.currentSort = state.previousSort;
    updateUIForCurrentSort();

    // Show error toast
    showErrorToast('Something went wrong. Please try again.');
  }

  /**
   * Show error toast
   */
  function showErrorToast(message) {
    const toast = document.createElement('div');
    toast.className = 'sort-error-toast';
    toast.setAttribute('role', 'alert');
    toast.textContent = message;
    
    document.body.appendChild(toast);

    // Auto-dismiss
    setTimeout(() => {
      toast.remove();
    }, CONFIG.timings.errorDismiss);
  }

  /**
   * Scroll to product grid
   */
  function scrollToGrid() {
    if (!elements.productsGrid) return;

    // Check for reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    elements.productsGrid.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start'
    });
  }

  /**
   * Announce sort to screen readers
   */
  function announceSort(sortValue) {
    if (!elements.announcement) return;

    const sortLabel = getSortLabel(sortValue);
    const productCount = elements.toolbar?.querySelector('[data-product-count]')?.textContent || '';
    
    elements.announcement.textContent = `Products sorted by ${sortLabel}. Showing ${productCount} products.`;
  }

  /**
   * Track GA4 event (dataLayer only: sent once by GTM tag "GA4 - Sort Interactions";
   * with a gtag call as well, every sort event was counted twice)
   */
  function trackGA4Event(eventName, additionalParams = {}) {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) return;
    const deviceCategory = state.isMobile ? 'mobile' : 'desktop';
    // Filters in the URL, e.g. "product_size:M|choose_colors:Navy" (GA4 values capped at 100 chars)
    const activeFilters = [];
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (key.indexOf('filter.') === 0) activeFilters.push(key.split('.').pop() + ':' + value);
    });
    const payload = {
      event_category: 'collection_interaction',
      collection_handle: state.collectionHandle,
      device_category: deviceCategory,
      device_type: window.innerWidth < 750 ? 'mobile' : 'desktop',
      active_filters: activeFilters.join('|').slice(0, 100),
      ...additionalParams
    };
    if (window.dataLayer) {
      window.dataLayer.push({ event: eventName, ...payload });
    }
  }

  /**
   * Handle keyboard navigation
   */
  function handleTriggerKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleDropdown(e);
    }
  }

  function handleOptionKeydown(e, option) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOptionSelect(option);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusNextOption(option);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusPreviousOption(option);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown();
    }
  }

  function focusNextOption(currentOption) {
    const options = Array.from(elements.dropdown.querySelectorAll(CONFIG.selectors.dropdownOption));
    const currentIndex = options.indexOf(currentOption);
    const nextOption = options[currentIndex + 1] || options[0];
    nextOption.focus();
  }

  function focusPreviousOption(currentOption) {
    const options = Array.from(elements.dropdown.querySelectorAll(CONFIG.selectors.dropdownOption));
    const currentIndex = options.indexOf(currentOption);
    const prevOption = options[currentIndex - 1] || options[options.length - 1];
    prevOption.focus();
  }

  function handleClickOutside(e) {
    if (!elements.dropdown || elements.dropdown.hidden) return;
    if (!elements.trigger) return;

    if (!elements.trigger.contains(e.target) && !elements.dropdown.contains(e.target)) {
      closeDropdown();
    }
  }

  function handleEscapeKey(e) {
    if (e.key === 'Escape') {
      if (elements.dropdown && !elements.dropdown.hidden) {
        closeDropdown();
      }
      if (elements.modal && !elements.modal.hidden) {
        closeModal();
      }
    }
  }

  /**
   * Debounce utility
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section load (Theme Editor)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector(CONFIG.selectors.toolbar)) {
      init();
    }
  });

})();
